import { Link } from "react-router-dom";
import PiCheckTickSquareSolid from "@markeroo/icons/src/solid/general/PiCheckTickSquareSolid";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import PiClockDefaultStroke from "@markeroo/icons/src/stroke/time/PiClockDefaultStroke";
import PiLightningThunderElectricOnStroke from "@markeroo/icons/src/stroke/weather/PiLightningThunderElectricOnStroke";
import PiCheckTickCircleStroke from "@markeroo/icons/src/stroke/general/PiCheckTickCircleStroke";
import { MarkeerooBrand } from "@/components/MarkeerooBrand";
import { ui } from "@/strings";

const logoUrl = new URL("../../images/logo-full.png", import.meta.url).href;
const heroUrl = new URL("../../images/hero.png", import.meta.url).href;

/* eslint-disable no-restricted-syntax -- Marketing page with static text */

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-border bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={logoUrl} alt="Markeroo" className="h-8 w-auto sm:h-10" />
            <MarkeerooBrand className="hidden text-2xl sm:block" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-6">
            <a href="#how-it-works" className="hidden text-sm font-medium text-brand-900 hover:text-brand-950 sm:block">
              How it Works
            </a>
            <a href="#pricing" className="hidden text-sm font-medium text-brand-900 hover:text-brand-950 sm:block">
              Pricing
            </a>
            <Link to="/login" className="text-sm font-medium text-brand-900 hover:text-brand-950">
              Log In
            </Link>
            <Link
              to="/register"
              className="whitespace-nowrap rounded-lg bg-highlight px-3 py-2 text-xs font-semibold text-text-theme-on-primary transition hover:bg-highlight-hover sm:px-4 sm:text-sm"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-50/50 to-white px-4 py-12 sm:px-6 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-start gap-8 sm:gap-12 lg:grid-cols-2">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-brand-950 sm:text-3xl md:text-4xl">
                Better feedback for students.
                <br />
                Saner workloads for teachers.
              </h1>
              <p className="mt-4 text-base text-brand-900 sm:mt-6 sm:text-base md:text-lg">
                Markeroo is the ELA teaching assistant that reads, highlights, and generates feedback based on{" "}
                <em>your</em> grade-level standards. You define the criteria; Markeroo finds the evidence.
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:items-center sm:gap-4">
                <Link
                  to="/register"
                  className="inline-flex w-full items-center justify-center rounded-lg bg-highlight px-6 py-3 text-sm font-semibold text-text-theme-on-primary transition hover:bg-highlight-hover sm:w-auto sm:text-base"
                >
                  Try Markeroo for Free
                </Link>
                <span className="text-xs text-center text-text-theme-muted sm:text-left sm:text-sm">
                  Tuned for Grades 3-12 · No credit card required
                </span>
              </div>
            </div>
            <div className="relative -mt-8 sm:mt-0">
              <img
                src={heroUrl}
                alt="Markeroo highlighting a student paper with color-coded feedback"
                className="w-full max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="bg-surface-secondary px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-base font-medium text-brand-900 sm:text-lg">
            Helping teachers reclaim their weekends <strong>and</strong> giving students actionable feedback.
          </p>
          <div className="mt-8 grid gap-8 sm:grid-cols-3">
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-semantic-processing-bg" aria-hidden="true">
                <img src="/images/peep-3.svg" alt="" className="h-14 w-14 object-cover object-top" />
              </div>
              <p className="text-sm italic text-brand-900">"This is a total game-changer!"</p>
              <p className="text-xs font-medium text-text-theme-muted">— Mr. W, 5th Grade</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-semantic-success-bg" aria-hidden="true">
                <img src="/images/peep-2.svg" alt="" className="h-14 w-14 object-cover object-top" />
              </div>
              <p className="text-sm italic text-brand-900">"Consistent feedback for every student."</p>
              <p className="text-xs font-medium text-text-theme-muted">— Ms. Lee, 8th Grade</p>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-brand-100" aria-hidden="true">
                <img src="/images/peep-1.svg" alt="" className="h-14 w-14 object-cover object-top" />
              </div>
              <p className="text-sm italic text-brand-900">"Markeroo saved my grading life!"</p>
              <p className="text-xs font-medium text-text-theme-muted">— Mrs. Davis, 10th Grade</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Why Section */}
      <section className="px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-xl font-bold text-brand-950 sm:text-2xl">Why Markeroo?</h2>
          <div className="mt-8 grid gap-6 sm:mt-12 sm:grid-cols-3 sm:gap-8">
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-surface-secondary">
                <PiClockDefaultStroke size={32} className="text-brand-900" />
              </div>
              <h3 className="mt-4 font-semibold text-brand-950">The Problem</h3>
              <p className="mt-2 text-sm text-brand-900">
                Exhausted teachers staring at a stack of 120 essays at 9 PM.
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-100">
                <PiLightningThunderElectricOnStroke size={32} className="text-brand-600" />
              </div>
              <h3 className="mt-4 font-semibold text-brand-950">The Markeroo Solution</h3>
              <p className="mt-2 text-sm text-brand-900">
                Batch process an entire class in minutes. Markeroo does the "first pass" so you can focus on the nuance.
              </p>
            </div>
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-semantic-success-bg">
                <PiCheckTickCircleStroke size={32} className="text-semantic-success" />
              </div>
              <h3 className="mt-4 font-semibold text-brand-950">The Result</h3>
              <p className="mt-2 text-sm text-brand-900">
                Consistent, equitable feedback for Student #1 and Student #30.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase */}
      <section className="bg-brand-50 px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 sm:text-sm">Feature Showcase</p>
            <h2 className="mt-2 text-xl font-bold text-brand-950 sm:text-2xl">Deeply Aligned to ELA Standards</h2>
            <p className="mt-4 text-sm text-brand-900 sm:text-base">Built for Your Classroom Standards</p>
          </div>
          <div className="mt-8 grid gap-6 sm:mt-12 sm:grid-cols-3 sm:gap-8">
            <div className="rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h3 className="text-sm font-semibold text-brand-950 sm:text-base">Ready-to-use Templates</h3>
              <p className="mt-2 text-xs text-brand-900 sm:text-sm">
                Choose from pre-built frameworks for argument, informative, or narrative writing.
              </p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h3 className="text-sm font-semibold text-brand-950 sm:text-base">Customizable Criteria</h3>
              <p className="mt-2 text-xs text-brand-900 sm:text-sm">
                Add your own focus areas: "varied sentence structure" or "cites at least two sources."
              </p>
            </div>
            <div className="rounded-lg bg-white p-4 shadow-sm sm:p-6">
              <h3 className="text-sm font-semibold text-brand-950 sm:text-base">Draft vs. Final Mode</h3>
              <p className="mt-2 text-xs text-brand-900 sm:text-sm">
                Toggle between explanations only and specific revision suggestions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="px-4 py-12 sm:px-6 sm:py-16" tabIndex={-1}>
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-xl font-bold text-brand-950 sm:text-2xl">How It Works</h2>
          <div className="mt-8 space-y-8 sm:mt-12 sm:space-y-12">
            <div className="flex gap-3 sm:gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white sm:h-10 sm:w-10 sm:text-lg">
                1
              </div>
              <div>
                <h3 className="text-base font-semibold text-brand-950 sm:text-lg">Set Your Criteria</h3>
                <p className="mt-1 text-sm text-brand-900 sm:text-base">
                  Select your grade level and writing type, or write your own custom criteria. Markeroo knows what to look for.
                </p>
              </div>
            </div>
            <div className="flex gap-3 sm:gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white sm:h-10 sm:w-10 sm:text-lg">
                2
              </div>
              <div>
                <h3 className="text-base font-semibold text-brand-950 sm:text-lg">Markeroo Reads & Highlights</h3>
                <p className="mt-1 text-sm text-brand-900 sm:text-base">
                  Markeroo scans every paper instantly. It color-codes the text where students met the criteria—and where they missed the mark.
                </p>
              </div>
            </div>
            <div className="flex gap-3 sm:gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white sm:h-10 sm:w-10 sm:text-lg">
                3
              </div>
              <div>
                <h3 className="text-base font-semibold text-brand-950 sm:text-lg">You Review & Refine (Total Control)</h3>
                <p className="mt-1 text-sm text-brand-900 sm:text-base">
                  You are the teacher. Edit the highlights, add your own comments, or tweak the generated summary. Markeroo does a first pass so you can focus on giving each student exactly what they need.
                </p>
              </div>
            </div>
            <div className="flex gap-3 sm:gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white sm:h-10 sm:w-10 sm:text-lg">
                4
              </div>
              <div>
                <h3 className="text-base font-semibold text-brand-950 sm:text-lg">Share with One Click</h3>
                <p className="mt-1 text-sm text-brand-900 sm:text-base">
                  Send a secure link to students (and parents). No student login required. They see their paper, the colorful highlights, and a clear "Next Steps" summary.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-surface-secondary px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-xl font-bold text-brand-950 sm:text-2xl">The Difference</h2>
          <div className="mt-6 overflow-x-auto sm:mt-8">
            <div className="inline-block min-w-full overflow-hidden rounded-xl bg-white text-left shadow-sm ring-1 ring-border">
              <table className="min-w-full">
                <caption className="sr-only">Comparison between traditional grading and Markeroo</caption>
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-1/2 px-3 py-3 text-center text-xs font-semibold text-text-theme-muted sm:px-6 sm:py-4 sm:text-sm">Without Markeroo</th>
                    <th className="w-1/2 px-3 py-3 text-center text-xs font-semibold text-brand-600 sm:px-6 sm:py-4 sm:text-sm">With Markeroo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiMultipleCrossCancelDefaultStroke size={20} className="mr-1 inline text-border-hover sm:mr-2" aria-hidden="true" />10-15 minutes per paper</td>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiCheckTickSquareSolid size={20} className="mr-1 inline text-semantic-success sm:mr-2" aria-hidden="true" />2 minutes to review & personalize</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiMultipleCrossCancelDefaultStroke size={20} className="mr-1 inline text-border-hover sm:mr-2" aria-hidden="true" />Feedback quality drops after hour 3</td>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiCheckTickSquareSolid size={20} className="mr-1 inline text-semantic-success sm:mr-2" aria-hidden="true" />Consistent feedback for every student</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiMultipleCrossCancelDefaultStroke size={20} className="mr-1 inline text-border-hover sm:mr-2" aria-hidden="true" />"Good job" or "Expand this"</td>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiCheckTickSquareSolid size={20} className="mr-1 inline text-semantic-success sm:mr-2" aria-hidden="true" />Specific, evidence-based suggestions</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiMultipleCrossCancelDefaultStroke size={20} className="mr-1 inline text-border-hover sm:mr-2" aria-hidden="true" />Red ink fatigue—feedback gets ignored</td>
                    <td className="px-3 py-3 text-xs text-brand-900 sm:px-6 sm:py-4 sm:text-sm"><PiCheckTickSquareSolid size={20} className="mr-1 inline text-semantic-success sm:mr-2" aria-hidden="true" />Color-coded highlights feel engaging</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-4 py-12 sm:px-6 sm:py-16" tabIndex={-1}>
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-xl font-bold text-brand-950 sm:text-2xl">Pricing</h2>
          <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-brand-900 sm:mt-4 sm:text-base">
            Start with a 30-day free trial. No credit card required.
          </p>
          <div className="mt-8 grid gap-6 sm:mt-12 sm:grid-cols-2 sm:gap-8">
            <div className="flex flex-col rounded-xl border-2 border-brand-600 bg-white p-6 sm:p-8">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-brand-950 sm:text-xl">30-Day Free Trial</h3>
                <span className="rounded-full bg-brand-100 px-2 py-1 text-xs font-medium text-brand-700">
                  Start Here
                </span>
              </div>
              <p className="mt-2 text-xs text-brand-900 sm:text-sm">Try all Pro features free</p>
              <ul className="mt-4 flex-1 space-y-2 sm:mt-6 sm:space-y-3">
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Highlight up to 50 papers per month
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  All Pro features included
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  No credit card required
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Downgrades to free plan after 30 days
                </li>
              </ul>
              <Link
                to="/register"
                className="mt-6 block w-full rounded-lg bg-brand-600 py-3 text-center text-sm font-semibold text-white transition hover:bg-brand-700 sm:mt-8"
              >
                Start Free Trial
              </Link>
            </div>
            <div className="flex flex-col rounded-xl border border-border bg-white p-6 sm:p-8">
              <h3 className="text-lg font-semibold text-brand-950 sm:text-xl">Pro Teacher</h3>
              <p className="mt-2 text-brand-900">
                <span className="text-xl font-bold text-brand-950 sm:text-2xl">$10</span>
                <span className="text-xs sm:text-sm">/month with annual subscription</span>
                <br />
                <span className="text-xs text-text-theme-muted">$15/month if paid monthly</span>
              </p>
              <ul className="mt-4 flex-1 space-y-2 sm:mt-6 sm:space-y-3">
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Unlimited paper highlights
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Create custom criteria
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Google Classroom integration
                </li>
                <li className="flex items-center gap-2 text-xs text-brand-900 sm:text-sm">
                  <PiCheckTickSquareSolid size={16} className="flex-shrink-0 text-semantic-success" />
                  Priority support
                </li>
              </ul>
              <Link
                to="/register?next=/pricing"
                className="mt-6 block w-full rounded-lg border border-border py-3 text-center text-sm font-semibold text-brand-900 transition hover:bg-surface-hover sm:mt-8"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-white px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="Markeroo" className="h-7 w-auto sm:h-8" />
              <MarkeerooBrand className="text-lg sm:text-xl" />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-text-theme-muted sm:gap-6 sm:text-sm">
              <a href={ui.contact.supportEmailHref} className="hover:text-brand-900">
                Support
              </a>
              <Link to="/privacy-policy" className="hover:text-brand-900">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="hover:text-brand-900">
                Terms of Service
              </Link>
              <Link to="/security" className="hover:text-brand-900">
                Security
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
